import { type AmenityHighlight, getPublicListingName } from './listing-metadata';
import { hasBookableTourProvider, type InstantTourProvidersLike } from '@/src/lib/utils/instantTourProviders';
import { BOOK_TOUR_HASH } from '@/src/lib/constants';

type Breadcrumb = { label: string; url?: string; href?: string };

type ListingJsonLdAddress = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  hideStreet?: boolean;
};

export type ListingJsonLdPhoto = { url?: string; caption?: string };

type ListingJsonLdCoordinates = { lat?: number; lon?: number };

export type ListingJsonLdInventory = {
  totalUnitCount?: number;
  availableUnitCount?: number;
};

/**
 * Structural subset of PropertyDetailListing needed to emit listing schema.
 * All fields are optional — HDP listing payloads are Partial<ListingDetails>.
 */
export type ListingJsonLdListing = {
  displayName?: string;
  title?: string;
  propertyType?: string;
  building?: boolean;
  isApartmentBldg?: boolean;
  address?: ListingJsonLdAddress;
  geo?: ListingJsonLdCoordinates;
  largePhotos?: Array<ListingJsonLdPhoto>;
  photos?: Array<ListingJsonLdPhoto>;
  previewPhoto?: ListingJsonLdPhoto;
  medPhotoUrls?: Array<string>;
  listingMinMaxPriceBeds?: {
    minPrice?: number;
    maxPrice?: number;
    minBeds?: number;
    maxBeds?: number;
    minBaths?: number;
    maxBaths?: number;
    minSqft?: number;
    maxSqft?: number;
  };
  modelsAndPricing?: Array<{ numBeds?: number; lowPrice?: number; highPrice?: number }>;
  amenities?: { amenities?: Array<string>; highlights?: Array<AmenityHighlight> };
  details?: {
    tourUrls?: Array<string>; // 3DModel.contentUrl, one node per url
    tourLabels?: Array<string>; // 3DModel.name
    instantTourProviders?: InstantTourProvidersLike | null; // presence gates tourBookingPage
  };
};

function cleanString(value: string | undefined): string | undefined {
  const cleaned = value?.trim();
  return cleaned || undefined;
}

function includesStreetText(value: string, street: string): boolean {
  const normalize = (text: string) => text.toLowerCase().replace(/\s+/g, ' ').trim();
  return normalize(value).includes(normalize(street));
}

/**
 * Returns a listing name that is safe to publish. Listings with an undisclosed
 * address must not fall back to, or use a title that contains, the street.
 */
export function publicListingName(listing: Pick<ListingJsonLdListing, 'address' | 'displayName' | 'title'>) {
  const street = cleanString(listing.address?.street);
  const hideStreet = listing.address?.hideStreet === true;

  for (const value of [listing.displayName, listing.title]) {
    const candidate = cleanString(value);
    if (candidate && (!hideStreet || !street || !includesStreetText(candidate, street))) {
      return candidate;
    }
  }

  return hideStreet ? undefined : street;
}

function validGeoCoordinates(coordinates: ListingJsonLdCoordinates | undefined): { lat: number; lon: number } | undefined {
  const lat = coordinates?.lat;
  const lon = coordinates?.lon;

  if (
    typeof lat !== 'number' ||
    typeof lon !== 'number' ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lon) ||
    lat < -90 ||
    lat > 90 ||
    lon < -180 ||
    lon > 180 ||
    (lat === 0 && lon === 0)
  ) {
    return undefined;
  }

  return { lat, lon };
}

/** Escapes JSON-LD for safe insertion into an inline script element. */
export function serializeJsonLd(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

const PROPERTY_TYPE_TO_SCHEMA_TYPE: Record<string, string> = {
  house: 'House',
  townhouse: 'House',
  condo: 'Apartment',
  divided: 'Apartment',
  medium: 'ApartmentComplex',
  large: 'ApartmentComplex',
  garden: 'ApartmentComplex',
  land: 'Residence',
};

function listingSchemaType(listing: ListingJsonLdListing, isBuildingPage: boolean): string {
  if (isBuildingPage) return 'ApartmentComplex';
  const propertyType = listing.propertyType?.toLowerCase();
  if (propertyType) return PROPERTY_TYPE_TO_SCHEMA_TYPE[propertyType] || 'Apartment';
  return listing.building || listing.isApartmentBldg ? 'ApartmentComplex' : 'Apartment';
}

function listingPostalAddress(address: ListingJsonLdAddress | undefined) {
  if (!address) return undefined;
  return {
    '@type': 'PostalAddress',
    ...(address.street && !address.hideStreet ? { streetAddress: address.street } : {}),
    ...(address.city ? { addressLocality: address.city } : {}),
    ...(address.state ? { addressRegion: address.state } : {}),
    ...(address.zip ? { postalCode: address.zip } : {}),
    addressCountry: 'US',
  };
}

/**
 * Photo precedence shared by listing JSON-LD and HDP OG/Twitter metadata:
 * largePhotos → photos → previewPhoto → medPhotoUrls.
 */
export function listingImages(listing: ListingJsonLdListing): Array<string> {
  const photos = listing.largePhotos?.length ? listing.largePhotos : listing.photos;
  const urls = photos?.map((photo) => photo.url).filter((url): url is string => Boolean(url)) ?? [];
  if (urls.length > 0) return urls.slice(0, 5);
  if (listing.previewPhoto?.url) return [listing.previewPhoto.url];
  return listing.medPhotoUrls?.slice(0, 5) ?? [];
}

/**
 * The first photo selected by the shared listing-image precedence. Keeping the
 * metadata preview aligned with JSON-LD prevents social cards from describing
 * a different image than search engines index.
 */
export function listingPrimaryImage(listing: ListingJsonLdListing): ListingJsonLdPhoto | undefined {
  const photos = listing.largePhotos?.length ? listing.largePhotos : listing.photos;
  if (photos?.length) return photos.find((photo) => Boolean(photo.url));
  if (listing.previewPhoto?.url) return listing.previewPhoto;
  const url = listing.medPhotoUrls?.[0];
  return url ? { url } : undefined;
}

function listingPriceRange(listing: ListingJsonLdListing): { low: number; high: number } | null {
  const minMax = listing.listingMinMaxPriceBeds;
  const minPrice = validPrice(minMax?.minPrice);
  if (minPrice !== undefined) {
    return { low: minPrice, high: Math.max(validPrice(minMax?.maxPrice) ?? minPrice, minPrice) };
  }
  const modelPrices = (listing.modelsAndPricing ?? [])
    .flatMap((model) => [model.lowPrice, model.highPrice])
    .map(validPrice)
    .filter((price): price is number => price !== undefined);
  if (modelPrices.length === 0) return null;
  return { low: Math.min(...modelPrices), high: Math.max(...modelPrices) };
}

function validPrice(value: number | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * One 3DModel node per tour URL (`details.tourUrls`/`tourLabels` are the v3
 * adapter's pre-deduped parallel arrays). 3D tours are deliberately NOT the
 * legacy virtual-Event markup, which Google's event content guidelines now
 * disallow (HP-8483).
 */
function listingTour3dModels(listing: ListingJsonLdListing) {
  return tour3dModelNodes(listing.details?.tourUrls ?? [], listing.details?.tourLabels ?? []);
}

function tour3dModelNodes(urls: Array<string | undefined>, labels: Array<string | undefined>) {
  return urls.flatMap((rawUrl, index) => {
    const url = cleanString(rawUrl);
    if (!url) return [];
    return [{ '@type': '3DModel', name: cleanString(labels[index]) ?? '3D tour', contentUrl: url }];
  });
}

function knownUnitCount(value: number | undefined, allowZero: boolean): number | undefined {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) return undefined;
  return value > 0 || (allowZero && value === 0) ? value : undefined;
}

function listingAmenityFeatures(listing: ListingJsonLdListing) {
  return (listing.amenities?.amenities ?? [])
    .map(cleanString)
    .filter((name): name is string => Boolean(name))
    .map((name) => ({ '@type': 'LocationFeatureSpecification', name }));
}

/**
 * Listing schema for HDP pages: an Accommodation-family node (Apartment /
 * House / ApartmentComplex) with address + GeoCoordinates, plus a Product
 * node carrying the rental Offer so price surfaces in rich results.
 *
 * `fallbackGeo` covers HDP payloads whose coordinates live beside the listing
 * (the fetcher's top-level `geo`) rather than on it.
 */
export function buildListingJsonLd(
  listing: ListingJsonLdListing,
  url: string,
  fallbackGeo?: ListingJsonLdCoordinates,
  inventory?: ListingJsonLdInventory,
  isBuildingPage = false,
) {
  const name = getPublicListingName(listing);
  const address = listingPostalAddress(listing.address);
  const images = listingImages(listing);
  const tour3dModels = listingTour3dModels(listing);
  const geoSource = validGeoCoordinates(listing.geo) ?? validGeoCoordinates(fallbackGeo);
  const geo = geoSource
    ? { '@type': 'GeoCoordinates', latitude: geoSource.lat, longitude: geoSource.lon }
    : undefined;
  const minMax = listing.listingMinMaxPriceBeds;
  const price = listingPriceRange(listing);
  const schemaType = listingSchemaType(listing, isBuildingPage);
  const isBuilding = schemaType === 'ApartmentComplex';
  const totalUnitCount = knownUnitCount(inventory?.totalUnitCount, false);
  const availableUnitCount = knownUnitCount(inventory?.availableUnitCount, true);
  const availability = isBuilding
    ? availableUnitCount === undefined
      ? undefined
      : availableUnitCount > 0
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock'
    : 'https://schema.org/InStock';
  const amenityFeature = listingAmenityFeatures(listing);
  // tourBookingPage is only defined on Accommodation subtypes + ApartmentComplex,
  // not on bare Residence (the land mapping). Tours are booked on the HDP itself.
  const tourBookingPage =
    schemaType !== 'Residence' && hasBookableTourProvider(listing.details?.instantTourProviders)
      ? `${url}#${BOOK_TOUR_HASH}`
      : undefined;

  const residence = {
    '@type': schemaType,
    '@id': `${url}#residence`,
    name,
    url,
    ...(images.length > 0 ? { image: images } : {}),
    ...(address ? { address } : {}),
    ...(geo ? { geo } : {}),
    ...(totalUnitCount !== undefined ? { numberOfAccommodationUnits: totalUnitCount } : {}),
    ...(availableUnitCount !== undefined ? { numberOfAvailableAccommodationUnits: availableUnitCount } : {}),
    ...(minMax?.minBeds != null ? { numberOfBedrooms: minMax.minBeds } : {}),
    // numberOfBathroomsTotal and floorSize are Accommodation-only properties;
    // schema.org does not define them on ApartmentComplex.
    ...(!isBuilding && minMax?.minBaths ? { numberOfBathroomsTotal: minMax.minBaths } : {}),
    ...(!isBuilding && minMax?.minSqft
      ? { floorSize: { '@type': 'QuantitativeValue', value: minMax.minSqft, unitCode: 'FTK' } }
      : {}),
    ...(amenityFeature.length > 0 ? { amenityFeature } : {}),
    ...(tour3dModels.length > 0 ? { subjectOf: tour3dModels } : {}),
    ...(tourBookingPage ? { tourBookingPage } : {}),
  };

  const product = price
    ? {
        '@type': 'Product',
        '@id': `${url}#product`,
        name,
        url,
        ...(images.length > 0 ? { image: images } : {}),
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'USD',
          lowPrice: price.low,
          highPrice: price.high,
          ...(availability ? { availability } : {}),
          url,
        },
      }
    : null;

  return {
    '@context': 'https://schema.org',
    '@graph': product ? [residence, product] : [residence],
  };
}

/**
 * WebSite + Organization schema for the homepage.
 */
export function buildWebSiteJsonLd(origin: string) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: 'HotPads',
        alternateName: 'HotPads.com',
        url: `${origin}/`,
        logo: { '@type': 'ImageObject', url: `${origin}/icon.png` },
        sameAs: [
          'https://www.facebook.com/hotpads',
          'https://x.com/hotpads',
          'https://www.instagram.com/hotpads/',
          'https://www.linkedin.com/company/hotpads',
          'https://en.wikipedia.org/wiki/HotPads',
          'https://www.wikidata.org/wiki/Q5909627',
        ],
        parentOrganization: {
          '@type': 'Organization',
          name: 'Zillow Group',
          url: 'https://www.zillowgroup.com/',
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        name: 'HotPads',
        alternateName: 'HotPads.com',
        url: `${origin}/`,
        publisher: { '@id': `${origin}/#organization` },
      },
    ],
  };
}

/**
 * WebPage + breadcrumb schema for indexable static pages, tied to the
 * homepage's WebSite graph node.
 */
export function buildStaticWebPageJsonLd(origin: string, path: string, name: string, description: string) {
  const url = `${origin}${path}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name,
        description,
        isPartOf: { '@id': `${origin}/#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name },
        ],
      },
    ],
  };
}

type MinimalListing = {
  title?: string;
  uriMalone?: string;
  aliasEncoded?: string;
  medPhotoUrl?: string;
  medPhotoUrls?: Array<string>;
  propertyType?: string;
  opensBuildingPage?: boolean;
  has3dTour?: boolean;
  tourUrls?: Array<string>;
  tourLabels?: Array<string>;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    hideStreet?: boolean;
  };
};

export function buildBreadcrumbListJsonLd(items: Array<Breadcrumb>, origin: string) {
  const filtered = items.filter((item) => item.label);
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: filtered.map((item, index) => {
      const url = item.url ?? item.href;
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        ...(url ? { item: url.startsWith('http') ? url : `${origin}${url}` } : {}),
      };
    }),
  };
}

/**
 * Each ListItem nests a typed Accommodation-family entity under `item` (rather
 * than flattening name/address onto the ListItem, which doesn't define them).
 * The entity `@id` matches the HDP's `#residence` node so both pages describe
 * the same thing. Toured listings carry 3DModels via `subjectOf` — with
 * `contentUrl` when the V3 card provides tour urls (HP-8526); listings that
 * only signal a tour exists keep the bare node (HP-8483).
 */
export function buildItemListJsonLd(
  listings: Array<MinimalListing>,
  origin: string,
  listName: string,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: listings.length,
    itemListElement: listings.map((listing, index) => {
      const hdpUrl = listing.uriMalone || (listing.aliasEncoded ? `/listings/${listing.aliasEncoded}` : undefined);
      const url = hdpUrl ? `${origin}${hdpUrl}` : undefined;
      const photo = listing.medPhotoUrl || listing.medPhotoUrls?.[0];
      const address = listingPostalAddress(listing.address);
      const tourModels = tour3dModelNodes(listing.tourUrls ?? [], listing.tourLabels ?? []);
      const subjectOf = tourModels.length
        ? tourModels
        : listing.has3dTour
          ? [{ '@type': '3DModel', name: '3D tour' }]
          : [];
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': listingSchemaType(listing, Boolean(listing.opensBuildingPage)),
          ...(url ? { '@id': `${url}#residence`, url } : {}),
          name: publicListingName(listing) ?? 'Rental listing',
          ...(photo ? { image: photo } : {}),
          ...(address ? { address } : {}),
          ...(subjectOf.length ? { subjectOf } : {}),
        },
      };
    }),
  };
}
